import { ACTION_DETAILS, ACTION_RESULT, ACTION_TARGET, ACTION_TARGET_ID, ACTION_TYPE, AREA, DataSecurityLevel, EVENT_CATEGORY, EVENT_NAME, FIELD_NAME_SEPARATOR, ID, PAGE_APP_NAME, PAGE_NAME, PAGE_TYPE, SUB_AREA, } from '@office-start/telemetry-types';
import { convertRouteToPage, getFlattenedFieldName, getPageAppName, getPageType, } from '@office-start/telemetry-utility';
import { getCurrentRouteItem, isRouteInitialized, logEvent, } from '../TelemetryService';
/**
 * Logs User Action event with given properties. Every event will be stored as a Kusto table.
 *
 *  @param properties Properties pertaining to click event
 *                    - type: ActionType;
 *                    - id: string;
 *                    - area: Area;
 *                    - subArea?: string;
 *                    - result: ActionResult;
 *                    - target: ActionTarget;
 *                    - targetId?: string;
 *                    - eventName?: string;
 *  @param securityThreshold DataSecurityLevel corresponds to Windows PlatformDataCollectionLevel which are ignored outside UWP.
 *
 * Event naming convention: Action.(Action.Result).(Action.Object)
 *
 * For type = 'Click',
 * if this click within the Office Hub authenticated experience that directs the user to an application, it is Click Referral event, Action.Result should be `Refer`,
 * if it's non-referral event, Action.Result should be `Navigate` or other reasonable values (Pin, Close, Open, Delete, Create, New....)
 *
 * Click on AppTile resulting to referral to PowerpointOnline:
 * e.g. logUserAction( { type: 'Click',  id: 'someId', area: 'AppTiles', subArea: 'Other', result: 'Refer', target: 'PowerPointOnline'}) would be stored as a Kusto table "Office_Taos_Hub_Action_Refer_PowerPointOnline"
 *
 * Navigate and Click on Edgeworth:
 * e.g. logUserAction( { type: 'Click',  id: 'someId', area: 'Edgeworth', subArea: 'Other', result: 'Navigate', target: 'Mru', eventName: 'Edgeworth_Click'}) would be stored as a Kusto table "Office_Taos_Hub_Action_Navigate_Mru"
 *
 * NOTE: Scenario that user is navigating directly to a link would not count as a Click Referral but count as a PageView.
 *       There is a backlog item https://office.visualstudio.com/MetaOS/_workitems/edit/4995951 to track the origin of external navigation to our site
 *
 * TODO: Add more test cases
 *
 *
 */
export function logUserAction(properties, securityThreshold) {
    if (securityThreshold === void 0) { securityThreshold = DataSecurityLevel.Security; }
    var eventName = createEventName(properties);
    logEvent(eventName, generateActionParams(properties), securityThreshold);
}
export function generateActionParams(properties) {
    var _a, _b, _c, _d;
    var params = {};
    params[EVENT_CATEGORY] = "Action" /* EventCategory.Action */;
    params[getFlattenedFieldName([ACTION_DETAILS, ACTION_TYPE])] =
        (_a = properties.type) !== null && _a !== void 0 ? _a : 'Click';
    params[getFlattenedFieldName([ACTION_DETAILS, ID])] = properties.id;
    params[getFlattenedFieldName([ACTION_DETAILS, AREA])] = properties.area;
    params[getFlattenedFieldName([ACTION_DETAILS, SUB_AREA])] =
        (_b = properties.subArea) !== null && _b !== void 0 ? _b : 'Other';
    params[getFlattenedFieldName([ACTION_DETAILS, ACTION_RESULT])] =
        properties.result;
    params[getFlattenedFieldName([ACTION_DETAILS, ACTION_TARGET])] =
        properties.target;
    params[getFlattenedFieldName([ACTION_DETAILS, ACTION_TARGET_ID])] =
        (_c = properties.targetId) !== null && _c !== void 0 ? _c : '';
    params[EVENT_NAME] = (_d = properties.eventName) !== null && _d !== void 0 ? _d : '';
    if (isRouteInitialized()) {
        var pageName = convertRouteToPage(getCurrentRouteItem());
        params[getFlattenedFieldName([ACTION_DETAILS, PAGE_NAME])] = pageName;
        params[getFlattenedFieldName([ACTION_DETAILS, PAGE_TYPE])] = getPageType(pageName);
        params[getFlattenedFieldName([ACTION_DETAILS, PAGE_APP_NAME])] = getPageAppName(pageName);
    }
    return params;
}
export function isActionEvent(params) {
    return params[EVENT_CATEGORY] === "Action" /* EventCategory.Action */;
}
export function createEventName(properties) {
    /*
     * Event names must map to human readable forms that acceptable from the EXPORT perspective.
     *
     * Action.(Action.Result).(Action.Target)
     *
     * e.g. Office.Taos.Hub.Action.Refer.Sway: Referral Click to Sway
     *      Office.Taos.Hub.Action.Navigate.LeftNav: Click on Left Navigation Bar

    */
    return ['Action', properties.result, properties.target].join(FIELD_NAME_SEPARATOR);
}
//# sourceMappingURL=logUserAction.js.map